import GettingStartedDetailSection from "./GettingStartedDetailSection";
import * as React from "react";
import { BaseComponent } from "components/BaseComponent/BaseComponent";
import BusyIndicator from "components/BusyIndicator";
import * as cn from "classnames";
import { gettingStartedStatus, GettingStartedStatus } from "components/GettingStarted/gettingStartedStatus";
import { ProjectResource, ReleaseResource } from "client/resources";
import ExternalLink from "components/ExternalLink";
import { SectionStepLink } from "components/GettingStarted/SectionStepLink";

import styles = require("./style.less");
import routeLinks from "../../routeLinks";
import {RouteComponentProps, withRouter} from "react-router";
import getWhatsNext from "components/GettingStarted/WhatsNext/WhatsNext";

const imageOctopus = require("./Octopus.svg");
const imageEnvironments = require("./Environments.svg");
const imagePackage = require("./Package.svg");
const imageProject = require("./Project.svg");
const imageDeploy = require("./Deploy.svg");

interface GettingStartedSection {
    header: string;
    area: string;
    complete: boolean;
    image: string;
    help?: React.ReactNode;
    actions: Array<ActionLink | Or | LearnAbout>;
}
interface IActionLink {
    path?: string;
    text: string;
    note?: React.ReactNode;
    complete: boolean;
    enabled: boolean;
    items?: DropDownItem[];
}

interface DropDownItem {
    label: string;
    link: string;
}

class ActionLink implements IActionLink {
    public path?: string;
    public text: string;
    public note?: React.ReactNode;
    public complete: boolean;
    public enabled: boolean;
    public items?: DropDownItem[];

    constructor(item: IActionLink) {
        this.path = item.path;
        this.text = item.text;
        this.note = item.note;
        this.complete = item.complete;
        this.enabled = item.enabled;
        this.items = item.items;
    }
}

class Or { }

class LearnAbout {
    constructor(readonly text: string, readonly link: string, readonly note: string) { }
}

interface GettingStartedDetailsState {
    next: string;
    projects: ProjectResource[];
    releases: ReleaseResource[];
    sections: {
        readonly: boolean;
        items: GettingStartedSection[];
    };
}

interface GettingStartedDetailsProps {
    highlightSection?: string;
    onNavigating?(): void;
}

type CombinedProps = GettingStartedDetailsProps & RouteComponentProps<{}>;

class GettingStartedDetailsInternal extends BaseComponent<CombinedProps, GettingStartedDetailsState> {
    unsubscribe: () => void;

    constructor(props: CombinedProps) {
        super(props);

        this.state = {
            next: "",
            projects: [],
            releases: [],
            sections: null,
        };
    }

    async componentDidMount() {
        const tasksStatus = await gettingStartedStatus.status();

        this.refresh(tasksStatus);

        if (!tasksStatus.tasks.Deploy.DeployedRelease.IsComplete) {
            this.unsubscribe = gettingStartedStatus.subscribe(status => this.refresh(status));
        }
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }

    render() {
        const isInEnvironmentPage = this.props.location && this.props.location.pathname && this.props.location.pathname.endsWith("/environments");
        const whatsNext = getWhatsNext(isInEnvironmentPage, this.state.next, this.state.projects, this.state.releases, this.props.onNavigating);

        return <div className={cn(styles.container, !this.state.sections && styles.liftBusyIndicator)}>
            {!this.state.sections && <BusyIndicator show={true} inline={false} />}
            <h2>Welcome to Octopus Deploy</h2>
            <p style={{ textAlign: "center", width: "80%" }}>
                The steps below introduce all the key Octopus concepts so you can easily automate deployments for all your applications and
            services in a safe, reliable and repeatable way.
            </p>
            <div className={styles.gettingStartedSection}>
                {this.state.sections && this.state.sections.items.map(section =>
                    <GettingStartedDetailSection key={section.area} header={section.header} complete={section.complete}
                        area={section.area} imgSrc={section.image} highlightSection={this.props.highlightSection === section.area} >
                        <ul className={styles.actionsList}>
                            {section.actions.map((action, index) => {
                                if (action instanceof ActionLink) {
                                    return <li key={action.text} className={cn({
                                        [styles.edit]: !this.state.sections.readonly && !action.complete,
                                        [styles.disabled]: !this.state.sections.readonly && !action.enabled,
                                        [styles.complete]: !this.state.sections.readonly && action.complete,
                                        [styles.completeReadOnly]: this.state.sections.readonly
                                    })}>
                                        {!action.enabled && <span>{action.text}</span>}
                                        {action.enabled && <SectionStepLink onNavigating={this.onNavigating}
                                            text={action.text} path={action.path}
                                            /* given this is an onboarding we don't display more then 5 items in these dropdowns */
                                            items={action.items && action.items.slice(0, 5)} />}
                                        {action.note && <div className={styles.note}>{action.note}</div>}
                                    </li>;
                                }
                                if (action instanceof LearnAbout) {
                                    return <li key={action.text} className={styles.info}>
                                        <ExternalLink href={action.link} showIcon={false}>{action.text}</ExternalLink>
                                        <div className={styles.note}>{action.note}</div>
                                    </li>;
                                }
                                if (action instanceof Or) {
                                    return <li key={`${section.area}-${index}`}>
                                        <div style={{ marginBottom: "0.5rem", marginTop: "-0.2rem" }}
                                            className={cn(styles.note, { [styles.disabled]: this.state.sections.readonly })}>
                                            <strong>OR</strong>
                                        </div>
                                    </li>;
                                }
                            })}
                        </ul>
                        <span className={styles.help}>
                            {section.help}
                        </span>
                    </GettingStartedDetailSection>
                )}
            </div>
            <div className={styles.detailsWhatsNextContainer}>
                {whatsNext.element}
            </div>
        </div>;
    }

    private onNavigating = () => {
        if (this.props.onNavigating) {
            this.props.onNavigating();
        }
    }

    private refresh(status: GettingStartedStatus) {
        const deployRelease = (() => {
            const project = status.projects.find(p =>
                p.Id === status.releases[0].ProjectId
            );

            return routeLinks.project(project).root;
        }).bind(this);

        const deployMultipleRelease = (() => {
            let projectIds: string[] = [];

            projectIds = status.releases.reduce((state, r) => {
                if (!state.includes(r.ProjectId)) {
                    state.push(r.ProjectId);
                }

                return state;
            }, projectIds);

            return status.projects
                .filter(p => projectIds.includes(p.Id))
                .map(p => (
                    {
                        label: p.Name,
                        link: routeLinks.project(p).root
                    }
                ));
        }).bind(this);

        const sectionsList: GettingStartedSection[] = [
            {
                header: "1. Install and set up",
                area: "OCTOPUS SERVER",
                complete: true,
                image: imageOctopus,
                actions: [
                    new ActionLink({
                        complete: true,
                        enabled: true,
                        text: "Create home directory"
                    }), new ActionLink({
                        complete: true,
                        enabled: true,
                        text: "Configure service account"
                    }), new ActionLink({
                        complete: true,
                        enabled: true,
                        text: "Specify SQL Server details"
                    }), new ActionLink({
                        complete: true,
                        enabled: true,
                        text: "Set up web portal"
                    }), new ActionLink({
                        complete: true,
                        enabled: true,
                        text: "Select authentication mode"
                    }), new ActionLink({
                        complete: true,
                        enabled: true,
                        text: "Create Octopus admin"
                    })
                ]
            },
            {
                header: "2. Configure",
                area: "INFRASTRUCTURE",
                complete: status.tasks.Infrastructure.IsComplete,
                image: imageEnvironments,
                help: <span>
                    Learn more about <ExternalLink href="OnboardingEnvironmentsLearnMore">environments</ExternalLink>
                    , <ExternalLink href="OnboardingDeploymentTargetsLearnMore">targets</ExternalLink>
                    and <ExternalLink href="OnboardingAccountsLearnMore">accounts</ExternalLink>.
            </span>,
                actions: [
                    new ActionLink({
                        text: "Create environments",
                        path: routeLinks.infrastructure.environments.root,
                        complete: status.tasks.Infrastructure.CreatedEnvironment.IsComplete,
                        enabled: status.tasks.Infrastructure.CreatedEnvironment.IsEnabled,
                        note: "Environments help you organise targets into groups. e.g., Dev, Test and Production."
                    }),
                    new ActionLink({
                        text: "Add deployment target",
                        path: routeLinks.infrastructure.environments.root,
                        complete: status.tasks.Infrastructure.CreatedMachine.IsComplete,
                        enabled: status.tasks.Infrastructure.CreatedMachine.IsEnabled,
                        note: "Targets are the servers where your applications will be deployed."
                    }),
                    new Or(),
                    new ActionLink({
                        text: "Add an account",
                        path: routeLinks.infrastructure.accounts.root,
                        complete: status.tasks.Infrastructure.AddedAzureAccount.IsComplete,
                        enabled: status.tasks.Infrastructure.AddedAzureAccount.IsEnabled,
                        note: "Accounts can be associated with deployment targets and include Azure subscriptions, SSH keys, and usernames and passwords."
                    })
                ]
            },
            {
                header: "3. Application",
                area: "PACKAGING",
                complete: status.tasks.Package.IsComplete,
                image: imagePackage,
                help: <span>
                    Learn more about <ExternalLink href="OnboardingVersioningLearnMore">versioning</ExternalLink>
                    , <ExternalLink href="OnboardingPackagesLearnMore">packaging</ExternalLink>
                    and <ExternalLink href="OnboardingPackageRepositoriesLearnMore">external feeds</ExternalLink>.
                    </span>,
                actions: [
                    new LearnAbout(
                        "Learn about packaging and versioning",
                        "OnboardingPackagesLearnMore",
                        "Package and version your applications so they can be deployed."
                    ),
                    new ActionLink({
                        text: "Upload package",
                        path: routeLinks.library.builtInRepository.root,
                        complete: status.tasks.Package.UploadedPackage.IsComplete,
                        enabled: status.tasks.Package.UploadedPackage.IsEnabled,
                        note: "Push packages to the built-in repository manually or integrate with your existing build tool."
                    }),
                    new Or(),
                    new ActionLink({
                        text: "Add external feed",
                        path: routeLinks.library.feeds,
                        complete: status.tasks.Package.AddedFeed.IsComplete,
                        enabled: status.tasks.Package.AddedFeed.IsEnabled,
                        note: "Octopus can integrate with external package repositories like TeamCity and VSTS."
                    })
                ]
            },
            {
                header: "4. Create a",
                area: "PROJECT",
                complete: status.tasks.Project.IsComplete,
                image: imageProject,
                help: <span>
                    Learn more about <ExternalLink href="OnboardingProjectsLearnMore">projects</ExternalLink>
                    and <ExternalLink href="OnboardingDeploymentProcessLearnMore">deployment processes</ExternalLink>.
                    </span>,
                actions: [
                    new ActionLink({
                        text: "Create a project",
                        path: routeLinks.projects,
                        complete: status.tasks.Project.CreatedProject.IsComplete,
                        enabled: status.tasks.Project.CreatedProject.IsEnabled,
                        note: "Octopus projects specify the steps and configuration variables required to deploy your application."
                    }),
                    new ActionLink({
                        text: "Define its process",
                        path: status.projects.length === 1 ? routeLinks.project(status.projects[0]).process.root : null,
                        complete: status.tasks.Project.AddedProjectStep.IsComplete,
                        enabled: status.tasks.Project.AddedProjectStep.IsEnabled,
                        note: "A deployment process specifies the steps to deploy an application which allows you to deploy new versions consistently every time.",
                        items: status.projects.length > 1 ? status.projects.map(p => (
                            {
                                label: p.Name,
                                link: routeLinks.project(p).process.root
                            }
                        )) : null
                    })
                ]
            },
            {
                header: "5. You're ready to",
                area: "DEPLOY",
                complete: status.tasks.Deploy.IsComplete,
                image: imageDeploy,
                help: <span>Learn more about <ExternalLink href="OnboardingReleasesLearnMore">creating and deploying releases</ExternalLink>.</span>,
                actions: [
                    new ActionLink({
                        text: "Create a release",
                        path: status.projects.length === 1 ? routeLinks.project(status.projects[0]).releases : null,
                        complete: status.tasks.Deploy.CreatedRelease.IsComplete,
                        enabled: status.tasks.Deploy.CreatedRelease.IsEnabled,
                        note: (<div>Releases contain all project and package details so it can be deployed safely over and over.
                            <br /><br />
                            Creating a release can be done once steps 1-4 are complete.</div>),
                        items: status.projects.length > 1 ? status.projects.map(p => (
                            {
                                label: p.Name,
                                link: routeLinks.project(p).releases
                            }
                        )) : null
                    }),
                    new ActionLink({
                        text: "Deploy a release",
                        path: status.releases.length === 1 ? deployRelease() : null,
                        complete: status.tasks.Deploy.DeployedRelease.IsComplete,
                        enabled: status.tasks.Deploy.DeployedRelease.IsEnabled,
                        note: "Deploy your first release to complete your journey into deployment automation.",
                        items: status.releases.length > 1 ? deployMultipleRelease() : null
                    })
                ]
            }
        ];

        this.setState({
            sections: {
                readonly: status.tasks.Deploy.DeployedRelease.IsComplete,
                items: sectionsList
            },
            next: status.next,
            projects: status.projects,
            releases: status.releases
        });
    }

}

export const GettingStartedDetails = withRouter<GettingStartedDetailsProps>(GettingStartedDetailsInternal);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/GettingStarted/GettingStartedDetails.tsx